import type { Camera } from "../engine/camera.js";
import type { Renderer } from "../engine/renderer.js";
import { CombatEffects, type CombatParticle } from "./effects.js";
import type { CombatPoint } from "./model.js";

export interface RocketVisual extends CombatPoint { vx: number; vy: number; vz: number }
const QUAD_CAPACITY = 2048, STRIDE = 10;
const vertexSource = `#version 300 es
layout(location=0) in vec3 aPosition;
layout(location=1) in vec2 aUv;
layout(location=2) in vec4 aColor;
layout(location=3) in float aShape;
uniform mat4 uViewProjection;
out vec2 vUv; out vec4 vColor; out float vShape;
void main() { vUv=aUv; vColor=aColor; vShape=aShape; gl_Position=uViewProjection*vec4(aPosition,1.0); }`;
const fragmentSource = `#version 300 es
precision highp float;
in vec2 vUv; in vec4 vColor; in float vShape;
out vec4 outColor;
void main() {
  float radial=dot(vUv,vUv);
  float angle=atan(vUv.y,vUv.x);
  float noise=sin(vUv.x*13.0+sin(vUv.y*9.0))*sin(vUv.y*15.0+vUv.x*5.0);
  float softness=1.0-smoothstep(0.07,1.0,radial);
  if(vShape>4.5) {
    // Broken, earthy pressure front on the terrain; never an emissive force field.
    float edge=0.76+0.045*sin(angle*11.0)+0.025*sin(angle*19.0);
    softness=(1.0-smoothstep(0.055,0.19,abs(sqrt(radial)-edge)))*(0.70+0.30*sin(angle*17.0+noise));
  } else if(vShape>3.5) {
    softness=(1.0-smoothstep(0.65,0.86,abs(vUv.x)+abs(vUv.y)*0.8));
  } else if(vShape>2.5) {
    softness=(1.0-smoothstep(0.06,0.85,radial*(1.0+0.42*cos(angle*6.0))));
  } else if(vShape>1.5) {
    softness=(1.0-smoothstep(0.03,0.91,radial+noise*0.075))*(0.83+noise*0.17);
  } else if(vShape>0.5) {
    softness=1.0-smoothstep(0.2,1.0,abs(vUv.y));
  } else {
    softness*=0.86+noise*0.14;
  }
  float alpha=vColor.a*softness;
  if(alpha<0.006) discard;
  outColor=vec4(vColor.rgb,alpha);
}`;

/** Two draws at most: depth-tested smoke/dust/debris then additive fire/tracers/motors.
 * Every quad expands into one reusable CPU/GPU buffer, never one draw per particle. */
export class CombatEffectRenderer {
  private readonly vertices = new Float32Array(QUAD_CAPACITY * 6 * STRIDE);
  private readonly smoke: CombatParticle[] = [];
  private readonly program: WebGLProgram;
  private readonly vao: WebGLVertexArrayObject;
  private readonly buffer: WebGLBuffer;
  private readonly viewProjection: WebGLUniformLocation | null;
  private count = 0;
  drawCalls = 0; visibleParticles = 0; visibleRockets = 0;
  private camera: Camera | undefined;

  constructor(private readonly renderer: Renderer) {
    const gl = renderer.gl;
    this.program = createProgram(gl);
    const vao = gl.createVertexArray(), buffer = gl.createBuffer();
    if (!vao || !buffer) throw new Error("Combat effects buffer allocation failed");
    this.vao = vao; this.buffer = buffer;
    this.viewProjection = gl.getUniformLocation(this.program, "uViewProjection");
    gl.bindVertexArray(vao); gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
    gl.bufferData(gl.ARRAY_BUFFER, this.vertices.byteLength, gl.DYNAMIC_DRAW);
    for (const [location, size, offset] of [[0, 3, 0], [1, 2, 3], [2, 4, 5], [3, 1, 9]]) {
      gl.enableVertexAttribArray(location!); gl.vertexAttribPointer(location!, size!, gl.FLOAT, false, STRIDE * 4, offset! * 4);
    }
    gl.bindVertexArray(null);
    renderer.meshMemoryBytes += this.vertices.byteLength;
  }

  render(camera: Camera, effects: CombatEffects, rockets: Iterable<RocketVisual>, now: number): void {
    this.camera = camera; this.drawCalls = 0; this.visibleParticles = 0; this.visibleRockets = 0; this.count = 0;
    const gl = this.renderer.gl;
    const program = gl.getParameter(gl.CURRENT_PROGRAM) as WebGLProgram | null;
    const vao = gl.getParameter(gl.VERTEX_ARRAY_BINDING) as WebGLVertexArrayObject | null;
    const buffer = gl.getParameter(gl.ARRAY_BUFFER_BINDING) as WebGLBuffer | null;
    const blend = gl.isEnabled(gl.BLEND), depth = gl.isEnabled(gl.DEPTH_TEST), cull = gl.isEnabled(gl.CULL_FACE);
    const depthWrite = gl.getParameter(gl.DEPTH_WRITEMASK) as boolean;
    const srcRgb = gl.getParameter(gl.BLEND_SRC_RGB) as number, dstRgb = gl.getParameter(gl.BLEND_DST_RGB) as number;
    const srcAlpha = gl.getParameter(gl.BLEND_SRC_ALPHA) as number, dstAlpha = gl.getParameter(gl.BLEND_DST_ALPHA) as number;
    const eqRgb = gl.getParameter(gl.BLEND_EQUATION_RGB) as number, eqAlpha = gl.getParameter(gl.BLEND_EQUATION_ALPHA) as number;
    try {
      gl.useProgram(this.program); gl.bindVertexArray(this.vao); gl.bindBuffer(gl.ARRAY_BUFFER, this.buffer);
      gl.uniformMatrix4fv(this.viewProjection, false, camera.viewProjection);
      gl.enable(gl.BLEND); gl.enable(gl.DEPTH_TEST); gl.disable(gl.CULL_FACE); gl.depthMask(false);
      gl.blendEquation(gl.FUNC_ADD); gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
      this.smoke.length = 0;
      for (const p of effects.particles) if (p.smoke && now >= p.born && now - p.born < p.life) this.smoke.push(p);
      const eye = camera.position;
      this.smoke.sort((a, b) => ((b.x - eye[0]) ** 2 + (b.z - eye[2]) ** 2) - ((a.x - eye[0]) ** 2 + (a.z - eye[2]) ** 2));
      for (const p of this.smoke) this.particle(p, now);
      for (const d of effects.debris) {
        const age = now - d.born;
        if (age < 0 || age >= d.life || !camera.isSphereVisible(d.x, d.y, d.z, d.size)) continue;
        this.visibleParticles++;
        this.billboard(d.x, d.y, d.z, d.size, d.red, d.green, d.blue, Math.min(1, (d.life - age) / 350), 4, d.rotation);
      }
      for (const wave of effects.shockwaves) {
        const age = now - wave.born;
        if (age < 0 || age >= wave.life) continue;
        const fraction = age / wave.life, radius = .5 + wave.radius * (1 - (1 - fraction) ** 2);
        if (!camera.isSphereVisible(wave.x, wave.y, wave.z, radius)) continue;
        this.quad(wave.x, wave.y, wave.z, radius, radius * wave.slopeX, 0, 0, radius * wave.slopeZ, radius, .69, .59, .43, .76 * (1 - fraction) ** .9, 5);
      }
      this.flush();
      gl.blendFunc(gl.SRC_ALPHA, gl.ONE);
      for (const p of effects.particles) if (!p.smoke && now >= p.born && now - p.born < p.life) this.particle(p, now);
      for (const b of effects.beams) {
        const age = now - b.born;
        if (age < 0 || age >= b.life) continue;
        const alpha = Math.max(0, 1 - age / b.life), length = Math.hypot(b.bx - b.ax, b.by - b.ay, b.bz - b.az);
        if (!camera.isSphereVisible((b.ax + b.bx) / 2, (b.ay + b.by) / 2, (b.az + b.bz) / 2, length / 2 + .2)) continue;
        // Ballistic light travels as a short segment; no whole-path offensive ray.
        const head = Math.min(1, age / b.life * 1.6 + .04), tail = Math.max(0, head - Math.min(.18, 4.5 / Math.max(1, length)));
        this.beam(b.ax + (b.bx - b.ax) * tail, b.ay + (b.by - b.ay) * tail, b.az + (b.bz - b.az) * tail,
          b.ax + (b.bx - b.ax) * head, b.ay + (b.by - b.ay) * head, b.az + (b.bz - b.az) * head,
          b.radius, 1, .70, .22, alpha);
      }
      for (const d of effects.debris) {
        const age = now - d.born;
        if (age < 0 || age >= Math.min(600, d.life) || !camera.isSphereVisible(d.x, d.y, d.z, d.size * 2)) continue;
        this.billboard(d.x, d.y, d.z, d.size * .7, 1, .32, .045, .75 * (1 - age / 600), 4, d.rotation);
      }
      for (const r of rockets) {
        if (!camera.isSphereVisible(r.x, r.y, r.z, 1)) continue;
        this.visibleRockets++;
        const scale = .72 / (Math.hypot(r.vx, r.vy, r.vz) || 1);
        this.beam(r.x - r.vx * scale, r.y - r.vy * scale, r.z - r.vz * scale, r.x, r.y, r.z, .085, 1, .62, .15, 1);
        this.billboard(r.x - r.vx * scale, r.y - r.vy * scale, r.z - r.vz * scale, .27, 1, .31, .03, .8);
      }
      this.flush();
    } finally {
      gl.depthMask(depthWrite); gl.blendFuncSeparate(srcRgb, dstRgb, srcAlpha, dstAlpha); gl.blendEquationSeparate(eqRgb, eqAlpha);
      if (blend) gl.enable(gl.BLEND); else gl.disable(gl.BLEND);
      if (depth) gl.enable(gl.DEPTH_TEST); else gl.disable(gl.DEPTH_TEST);
      if (cull) gl.enable(gl.CULL_FACE); else gl.disable(gl.CULL_FACE);
      gl.bindVertexArray(vao); gl.bindBuffer(gl.ARRAY_BUFFER, buffer); gl.useProgram(program);
    }
  }

  dispose(): void {
    const gl = this.renderer.gl;
    gl.deleteVertexArray(this.vao); gl.deleteBuffer(this.buffer); gl.deleteProgram(this.program);
    this.renderer.meshMemoryBytes -= this.vertices.byteLength;
  }
  private particle(p: CombatParticle, now: number): void {
    const elapsed = (now - p.born) / 1000, fraction = (now - p.born) / p.life;
    const x = p.x + p.vx * elapsed, y = p.y + p.vy * elapsed - p.gravity * elapsed * elapsed, z = p.z + p.vz * elapsed;
    const size = p.size + p.growth * elapsed;
    if (!this.camera!.isSphereVisible(x, y, z, size)) return;
    this.visibleParticles++;
    const alpha = p.alpha * (1 - fraction) ** (p.smoke ? .75 : .8) * (p.smoke ? Math.min(1, fraction * 18) : 1);
    if (p.gravity > 0) {
      const vy = p.vy - 2 * p.gravity * elapsed, tail = .024 * (1 - fraction);
      this.beam(x - p.vx * tail, y - vy * tail, z - p.vz * tail, x, y, z, size * .65, p.red, p.green, p.blue, alpha);
    } else this.billboard(x, y, z, size, p.red, p.green, p.blue, alpha, p.shape, p.rotation + elapsed * .3);
  }
  private billboard(x: number, y: number, z: number, size: number, r: number, g: number, b: number, alpha: number, shape = 0, rotation = 0): void {
    const view = this.camera!.view, c = Math.cos(rotation), s = Math.sin(rotation);
    this.quad(x, y, z, (view[0]! * c + view[1]! * s) * size, (view[4]! * c + view[5]! * s) * size, (view[8]! * c + view[9]! * s) * size,
      (view[1]! * c - view[0]! * s) * size, (view[5]! * c - view[4]! * s) * size, (view[9]! * c - view[8]! * s) * size, r, g, b, alpha, shape);
  }
  private beam(ax: number, ay: number, az: number, bx: number, by: number, bz: number, radius: number,
    r: number, g: number, b: number, alpha: number): void {
    const x = (ax + bx) / 2, y = (ay + by) / 2, z = (az + bz) / 2;
    const dx = (bx - ax) / 2, dy = (by - ay) / 2, dz = (bz - az) / 2;
    const eye = this.camera!.position, ex = eye[0] - x, ey = eye[1] - y, ez = eye[2] - z;
    let rx = dy * ez - dz * ey, ry = dz * ex - dx * ez, rz = dx * ey - dy * ex;
    const length = Math.hypot(rx, ry, rz) || 1; rx *= radius / length; ry *= radius / length; rz *= radius / length;
    this.quad(x, y, z, dx, dy, dz, rx, ry, rz, r, g, b, alpha, 1);
  }
  private quad(x: number, y: number, z: number, rx: number, ry: number, rz: number, ux: number, uy: number, uz: number,
    r: number, g: number, b: number, alpha: number, beam: number): void {
    if (this.count >= QUAD_CAPACITY) return;
    let offset = this.count++ * 6 * STRIDE;
    for (const [u, v] of CORNERS) {
      this.vertices[offset++] = x + rx * u + ux * v; this.vertices[offset++] = y + ry * u + uy * v; this.vertices[offset++] = z + rz * u + uz * v;
      this.vertices[offset++] = u; this.vertices[offset++] = v; this.vertices[offset++] = r; this.vertices[offset++] = g;
      this.vertices[offset++] = b; this.vertices[offset++] = alpha; this.vertices[offset++] = beam;
    }
  }
  private flush(): void {
    if (!this.count) return;
    const gl = this.renderer.gl;
    gl.bufferSubData(gl.ARRAY_BUFFER, 0, this.vertices, 0, this.count * 6 * STRIDE);
    gl.drawArrays(gl.TRIANGLES, 0, this.count * 6);
    this.renderer.metrics.drawCalls++; this.renderer.metrics.particleDrawCalls++;
    this.renderer.metrics.triangles += this.count * 2; this.renderer.metrics.particleCount += this.count;
    this.drawCalls++; this.count = 0;
  }
}
const CORNERS = [[-1, -1], [1, -1], [1, 1], [-1, -1], [1, 1], [-1, 1]] as const;
function createProgram(gl: WebGL2RenderingContext): WebGLProgram {
  const program = gl.createProgram();
  if (!program) throw new Error("Combat effects program allocation failed");
  for (const [type, source] of [[gl.VERTEX_SHADER, vertexSource], [gl.FRAGMENT_SHADER, fragmentSource]] as const) {
    const shader = gl.createShader(type);
    if (!shader) throw new Error("Combat effects shader allocation failed");
    gl.shaderSource(shader, source); gl.compileShader(shader);
    if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) throw new Error(gl.getShaderInfoLog(shader) ?? "Combat effects shader compilation failed");
    gl.attachShader(program, shader); gl.deleteShader(shader);
  }
  gl.linkProgram(program);
  if (!gl.getProgramParameter(program, gl.LINK_STATUS)) throw new Error(gl.getProgramInfoLog(program) ?? "Combat effects shader link failed");
  return program;
}
